import { useEffect, useState } from "react";
import { safeFetch } from "@/api/safeFetch";
import { Input, ButtonSiguiente } from "@/components/ui";
import { UsuarioDetalle } from "@/types";
import { useNavigate } from "react-router-dom";
import DeshabilitarUsuarioAdmin from "./DesabilitarUsuarioAdmin";
import RehabilitarUsuarioAdmin from "./RehabilitarUsuarioAdmin";
import DetalleUsuarioAdmin from "./DetalleUsuarioAdmin";

type VistaAdmin = "menu" | "deshabilitar" | "rehabilitar" | "detalle";

const GestionUsuarioAdmin: React.FC = () => {
  const [q, setQ] = useState("");
  const [usuario, setUsuario] = useState<UsuarioDetalle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [vista, setVista] = useState<VistaAdmin>("menu");

  const navigate = useNavigate();
  const token = localStorage.getItem("token") ?? "";

  // 🔍 Buscar usuario
  const buscar = async () => {
    if (!q.trim()) return;

    const controller = new AbortController();
    const signal = controller.signal;

    setLoading(true);
    setError(null);
    setUsuario(null);
    setVista("menu");

    try {
      const res = await safeFetch(
        `/api/admin/usuarios/detalle?q=${encodeURIComponent(q)}`,
        { headers: { Authorization: `Bearer ${token}` }, signal }
      );

      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json.message || "Usuario no encontrado");
      }

      const data: UsuarioDetalle = await res.json();
      setUsuario(data);
    } catch (err: any) {
      if (err.name !== "AbortError") {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }

    return () => controller.abort();
  };


  useEffect(() => {
    return () => {
      setQ("");
      setUsuario(null);
      setError(null);
      setLoading(false);
      setVista("menu");

      // limpiar scroll congelado
      window.scrollTo(0, 0);
    };
  }, []);


  return (
    <div className="w-full min-h-screen flex items-center justify-center bg-zinc-500 p-4 relative dark:text-zinc-700">

      {/* 🔙 Botón volver arriba izquierda */}
      <div className="absolute top-4 left-4">
        <ButtonSiguiente
          onClick={() => navigate("/backoffice")}
          className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition shadow"
        >
          ⬅ Volver
        </ButtonSiguiente>
      </div>

<div className="w-full max-w-3xl bg-zinc-400 p-6 rounded-xl shadow-md border-zinc-600 border-r-4 border-t-2 flex flex-col gap-6 shadow-purple-700 max-h-[90vh] overflow-y-auto">        <h2 className="text-2xl font-bold text-center">
          🔐 Gestión de usuario
        </h2>

        {/* BUSCADOR */}
        <div className="w-full bg-purple-600 p-4 rounded-xl flex gap-4 items-center border-white border-r-4 border-t-2 border shadow-md shadow-purple-700">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por DNI / NIE / email"
          />
          <ButtonSiguiente onClick={buscar}>Buscar</ButtonSiguiente>
        </div>

        {loading && <p>Buscando…</p>}
        {error && <p className="text-red-600 font-semibold">{error}</p>}

        {/* RESULTADO */}
        {usuario && vista === "menu" && (
          <div className="bg-white p-4 rounded shadow space-y-4">
            <h3 className="font-bold text-lg">👤 Usuario encontrado</h3>

            <p><b>ID:</b> {usuario.usuario_id}</p>
            <p><b>Usuario:</b> @{usuario.nombre_usuario}</p>
            <p><b>Email:</b> {usuario.email}</p>
            <p><b>Rol:</b> {usuario.roles}</p>

            <div className="flex gap-3">
              <ButtonSiguiente
                onClick={() => setVista("deshabilitar")}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                🚫 Deshabilitar
              </ButtonSiguiente>

              <ButtonSiguiente
                onClick={() => setVista("rehabilitar")}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                ✅ Rehabilitar
              </ButtonSiguiente>

              <ButtonSiguiente
                onClick={() => setVista("detalle")}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white"
              >
                📄 Detalle
              </ButtonSiguiente>
            </div>
          </div>
        )}

        {/* PLACEHOLDERS (los hacemos después) */}
        {usuario && vista === "deshabilitar" && (
          <DeshabilitarUsuarioAdmin
            usuario={usuario}
            onDone={() => {
              setVista("menu");
              buscar();
            }}
          />
        )}


        {usuario && vista === "rehabilitar" && (
          <RehabilitarUsuarioAdmin
            usuario={usuario}
            onDone={() => {
              setVista("menu");
              buscar();
            }}
          />
        )}


        {usuario && vista === "detalle" && (
          <DetalleUsuarioAdmin
            usuario={usuario}
            onBack={() => setVista("menu")}
          />
        )}

      </div>
    </div>
  );
};

export default GestionUsuarioAdmin;
